"use client";

import { useState } from "react";
import Link from "next/link";
import DeliveryTruckIcon from "@/components/icons/DeliveryTruckIcon";
import HeadsetIcon from "@/components/icons/HeadsetIcon";
import CheckIcon from "@/components/icons/CheckIcon";
import { Button } from "@/components";

type DeliveryMode = "same-day" | "mail-order";

const MODE_COPY: Record<
  DeliveryMode,
  {
    heading: string;
    description: string;
    details: string[];
    ctaLabel: string;
    ctaHref: string;
  }
> = {
  "same-day": {
    heading: "Daily Delivery: 9am\u201311pm",
    description:
      "Order before 11pm for same-day delivery. Late orders arrive next morning at 9am.",
    details: [
      "Rate: $9.99 delivery fee",
      "Min Order: $50 purchase",
      "Free Delivery: $99+",
      "Tracking: Real-time SMS",
    ],
    ctaLabel: "Order Before 3 PM",
    ctaHref: "/search",
  },
  "mail-order": {
    heading: "Mail Order Delivery",
    description:
      "Fast Alberta-wide cannabis shipping with discreet packaging and dependable tracking updates.",
    details: [
      "Rate: $14.99 shipping fee",
      "Min Order: $50 purchase",
      "Free Shipping: $199+",
      "Tracking: Canada Post updates",
    ],
    ctaLabel: "Shop Mail Order",
    ctaHref: "/search",
  },
};

export default function CalgaryDeliveryServiceSection() {
  const [mode, setMode] = useState<DeliveryMode>("same-day");
  const content = MODE_COPY[mode];

  return (
    <section
      className="w-full max-w-none"
      aria-labelledby="calgary-delivery-service-heading"
    >
      <div className="flex flex-col gap-4">
        {/* Header row */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h2
            id="calgary-delivery-service-heading"
            className="text-h3-mobile sm:text-h3-desktop font-bold text-text-primary"
          >
            Calgary weed delivery
          </h2>

          <div className="inline-flex lg:w-max w-full items-center rounded-xl border border-neutral-10 bg-constant-white p-1 gap-1">
            <Button
              variant={mode === "same-day" ? "primary" : "bordered"}
              size="lg"
              onClick={() => setMode("same-day")}
              className="rounded-lg px-4 py-2 max-sm:w-full"
            >
              Same-day delivery
            </Button>
            <Button
              variant={mode === "mail-order" ? "primary" : "bordered"}
              size="lg"
              onClick={() => setMode("mail-order")}
              className="rounded-lg px-4 py-2 max-sm:w-full"
            >
              Mail order delivery
            </Button>
          </div>
        </div>

        {/* Cards row */}
        <div className="grid gap-3 lg:grid-cols-[1.5fr_1fr]">
          {/* Left card, delivery info */}
          <article className="overflow-hidden rounded-2xl border border-blue-15 bg-constant-white shadow-blue-10 drop-shadow-lg">
            <div className="flex flex-col lg:flex-row">
              {/* Left column: icon + heading + desc + CTA */}
              <div className="flex flex-1 flex-col justify-between p-5 sm:p-6">
                <div>
                  <div className="mb-4 flex rounded-xl bg-blue-5 text-blue-80 p-4 w-max">
                    <DeliveryTruckIcon size={32} />
                  </div>

                  <h3 className="text-[22px] font-bold leading-tight text-blue-60 sm:text-h3-desktop">
                    {content.heading}
                  </h3>
                  <p className="mt-2 text-body-md font-medium leading-relaxed text-text-secondary">
                    {content.description}
                  </p>
                </div>

                <Button asChild variant="primary" size="lg" className="w-full mt-4 rounded-xl sm:w-max px-6 py-3">
                  <Link href={content.ctaHref}>
                    {content.ctaLabel}
                  </Link>
                </Button>
              </div>

              {/* Right column: checklist */}
              <div className="border-t border-neutral-10 p-5 sm:p-6 lg:border-l lg:border-t-0">
                <ul className="flex flex-col justify-center gap-4 lg:h-full">
                  {content.details.map((detail) => {
                    const [label, value] = detail.split(": ");
                    return (
                      <li
                        key={detail}
                        className="flex items-center gap-2.5 text-body-16-desktop"
                      >
                        <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-blue-60 text-constant-white">
                          <CheckIcon size={12} color="white" />
                        </span>
                        <span>
                          <span className="font-bold text-text-primary">
                            {label}:
                          </span>{" "}
                          <span className="font-medium text-text-primary">
                            {value}
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </article>

          {/* Right card, questions */}
          <article className="flex flex-col justify-between rounded-2xl border border-neutral-10 bg-constant-white p-5 sm:p-6">
            <div>
              <div className="mb-4 flex rounded-xl bg-blue-5 text-blue-80 p-4 w-max">
                <HeadsetIcon size={32} />
              </div>

              <h3 className="text-[22px] font-bold leading-tight text-blue-60 sm:text-h3-desktop">
                Have questions?
              </h3>
              <p className="mt-2 text-body-md font-medium leading-relaxed text-text-primary">
                Check our FAQ for instant answers about shipping, tracking, and
                returns before contacting support
              </p>
            </div>

            <div className="mt-5">
              <Button asChild variant="bordered" color="white" size="md">
                <Link href="/faq">
                  Read FAQ
                </Link>
              </Button>
            </div>
          </article>
        </div>

      </div>
    </section>
  );
}
